if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.product-carousel__track');
      if (!this.track?.children.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.items = [...this.track.children];
      this.active = this.items[0];
      this.emphasis = this.dataset.emphasis === 'true';
      this.loop = this.dataset.loop !== 'false' && this.items.length > 2;
      this.previous = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.status = this.querySelector('.product-carousel__status');
      this.progress = this.querySelector('.product-carousel__progress');
      this.mobileViewport = matchMedia('(max-width: 749px)');
      this.mobileViewport.addEventListener('change', () => this.updateNavigation(), options);
      this.updateNavigation();
      this.previous.addEventListener('click', () => this.step(-1), options);
      this.next.addEventListener('click', () => this.step(1), options);
      this.track.addEventListener('scroll', () => {
        if (this.hasAttribute('data-repositioning')) return;
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateActive());
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.settle(), 220);
      }, { ...options, passive: true });
      this.track.addEventListener('scrollend', () => this.settle(), options);
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.matches('input,select,textarea')) return;
        event.preventDefault();
        this.step((event.key === 'ArrowRight' ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1));
      }, options);
      this.track.addEventListener('focusin', event => {
        const item = event.target.closest('.product-carousel__slide');
        if (item && event.target.matches(':focus-visible') && !this.drag) this.center(item, true);
      }, options);
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select')) return;
        this.drag = { x: event.clientX, left: this.track.scrollLeft, id: event.pointerId };
        this.target = null;
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.drag) return;
        if (Math.abs(event.clientX - this.drag.x) > 6) {
          this.setAttribute('data-dragging', '');
          this.track.setPointerCapture(event.pointerId);
          this.suppressClick = true;
          this.track.scrollLeft = this.drag.left - (event.clientX - this.drag.x);
        }
      }, options);
      const stopDrag = () => {
        if (!this.drag) return;
        const wasDragging = this.hasAttribute('data-dragging');
        if (wasDragging) this.setAttribute('data-animating', '');
        this.drag = null;
        this.removeAttribute('data-dragging');
        setTimeout(() => { this.suppressClick = false; }, 0);
        this.updateActive();
        if (wasDragging) this.center(this.active, true);
      };
      this.track.addEventListener('pointerup', stopDrag, options);
      this.track.addEventListener('pointercancel', stopDrag, options);
      this.track.addEventListener('lostpointercapture', stopDrag, options);
      this.track.addEventListener('click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.observer = new ResizeObserver(() => {
        if (this.lastWidth === this.track.clientWidth) return;
        this.lastWidth = this.track.clientWidth;
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.center(this.active, false));
      });
      this.observer.observe(this.track);
      this.imageObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        // Decode once before recycled slides enter view; preserve each original form and image.
        this.querySelectorAll('img').forEach(img => { img.loading = 'eager'; img.decode?.().catch(() => {}); });
        this.imageObserver.disconnect();
      }, { rootMargin: '200px' });
      this.imageObserver.observe(this);
      this.setAttribute('data-ready', '');
      this.rebalance(true);
      // Let the browser finish its initial scroll-snap layout before selecting item one.
      this.initFrame = requestAnimationFrame(() => {
        this.center(this.items[0], false);
        this.updateActive();
      });
    }
    updateNavigation() {
      const navigation = this.dataset.navigationSplit === 'true'
        ? (this.mobileViewport.matches ? this.dataset.navigationMobile : this.dataset.navigationDesktop)
        : (this.dataset.navigation || 'progress');
      this.querySelector('.product-carousel__controls').hidden = this.items.length < 2 || !['arrows', 'both'].includes(navigation);
      if (this.progress) {
        this.progress.hidden = this.items.length < 2 || !['progress', 'both'].includes(navigation);
        this.progress.setAttribute('aria-valuemin', '0');
        this.progress.setAttribute('aria-valuemax', '100');
      }
    }
    center(item, smooth) {
      if (!item || !this.isConnected) return;
      const track = this.track.getBoundingClientRect();
      const slide = item.getBoundingClientRect();
      const delta = slide.left + slide.width / 2 - track.left - track.width / 2;
      if (Math.abs(delta) < 1) { this.removeAttribute('data-animating'); this.target = null; return; }
      const animate = smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (animate) { this.setAttribute('data-animating', ''); this.target = item; }
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: animate ? 'smooth' : 'instant' });
    }
    updateActive() {
      this.style.setProperty('--carousel-arrows-y', (this.track.offsetTop + this.track.offsetHeight / 2) + 'px');
      const rect = this.track.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const ordered = [...this.track.children];
      this.active = ordered.reduce((best, item) => {
        const r = item.getBoundingClientRect();
        const distance = Math.abs(r.left + r.width / 2 - center);
        return !best || distance < best.distance ? { item, distance } : best;
      }, null).item;
      const activeIndex = ordered.indexOf(this.active);
      ordered.forEach((item, i) => {
        item.toggleAttribute('data-active', i === activeIndex);
        item.toggleAttribute('data-near', Math.abs(i - activeIndex) === 1);
        const box = item.getBoundingClientRect();
        const base = this.emphasis ? (this.clientWidth >= 900 ? .86 : .92) : 1;
        const proximity = Math.max(0, 1 - Math.abs(box.left + box.width / 2 - center) / (box.width + 12));
        item.style.setProperty('--carousel-scale', (base + (1 - base) * proximity).toFixed(4));
      });
      this.status.textContent = `${this.items.indexOf(this.active) + 1} / ${this.items.length}`;
      if (this.progress) {
        const box = this.active.getBoundingClientRect();
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        const offset = direction * (center - box.left - box.width / 2) / (box.width + gap);
        const logical = this.items.indexOf(this.active) + offset;
        const progress = this.items.length > 1 ? Math.max(0, Math.min(1, logical / (this.items.length - 1))) : 1;
        this.progress.style.setProperty('--carousel-progress', progress.toFixed(5));
        this.progress.setAttribute('aria-valuenow', Math.round(progress * 100));
        this.progress.setAttribute('aria-valuetext', `${this.items.indexOf(this.active) + 1} / ${this.items.length}`);
      }
      this.previous.disabled = !this.loop && this.active === this.items[0];
      this.next.disabled = !this.loop && this.active === this.items.at(-1);
    }
    step(direction) {
      const current = this.items.indexOf(this.target || this.active);
      const index = this.loop ? (current + direction + this.items.length) % this.items.length : Math.max(0, Math.min(this.items.length - 1, current + direction));
      this.center(this.items[index], true);
    }
    settle() {
      if (this.drag || this.hasAttribute('data-repositioning') || !this.isConnected) return;
      this.updateActive();
      this.target = null;
      this.removeAttribute('data-animating');
      this.rebalance();
    }
    rebalance(force = false) {
      // Move existing slides, never clone forms or duplicate Shopify element IDs.
      if (!this.loop) return;
      const position = [...this.track.children].indexOf(this.active);
      if (!force && position > 1 && position < this.items.length - 2) return;
      this.setAttribute('data-repositioning', '');
      const middle = Math.floor(this.items.length / 2);
      let index = [...this.track.children].indexOf(this.active);
      const move = (node, before) => {
        if (node.contains(document.activeElement)) return false;
        if (this.track.moveBefore) this.track.moveBefore(node, before);
        else this.track.insertBefore(node, before);
        return true;
      };
      while (index < middle) { if (!move(this.track.lastElementChild, this.track.firstElementChild)) break; index++; }
      while (index > middle) { if (!move(this.track.firstElementChild, null)) break; index--; }
      this.center(this.active, false);
      this.updateActive();
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.removeAttribute('data-repositioning'));
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect();
      this.imageObserver?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.initFrame);
      cancelAnimationFrame(this.scrollFrame);
    }
  });
}
